import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import BookingButton from './BookingButton';

const SolutionSection=()=> {
const [ref,inView]=useInView({triggerOnce: true,threshold: 0.1});

const rootSkills=[ 
"Attention building: Help them focus and engage with the world",
"Working memory development: Build the ability to hold and use information",
"Sensory processing and integration: Make sense of sights,sounds,textures,and tastes",
"Interoception: Recognizing internal cues like hunger,fullness,and needing to use the bathroom",
"Oral-Motor Skills: Building strength & coordination for speech and safe eating (dysphagia)",
"Imitation: Copying actions and sounds,the engine of early learning" 
];

const leafSkills=[ 
"Communication foundation: From gestures to words to sentences that work",
"Feeding mastery: Safe,enjoyable eating without battles or fears",
"Self-regulation: Managing big emotions in little bodies",
"Social connection: Engaging with others in meaningful ways",
"Initiation: Using a sound,gesture,or word to get a need met",
"Cognitive Flexibility: Tolerating a change in routine or a new food on their plate" 
];

const flowerSkills=[ 
"Executive function preparation: Planning,problem-solving,and flexible thinking",
"Language complexity: Rich vocabulary and complex communication",
"Independence building: Self-help skills and confident exploration",
"Learning readiness: Foundation skills for academic success",
"Social Engagement: Playing with peers,taking turns,and making friends",
"Self-Feeding: confidently exploring and eating a variety of nutritious foods" 
];

return ( 
<section id="solution" className="py-20 bg-white" ref={ref}> 
<div className="max-w-6xl mx-auto px-6 lg:px-8"> 
<motion.div initial={{opacity: 0,y: 30}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{duration: 0.8}} className="text-center mb-16" > 
<h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-8 font-serif"> Build the Foundation Their Brain Needs to <span className="gradient-text">Thrive</span> </h2> 
</motion.div> 

<motion.div initial={{opacity: 0,y: 20}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{duration: 0.8,delay: 0.2}} className="max-w-4xl mx-auto mb-16 space-y-6 text-center" > 
<p className="text-lg text-gray-700 leading-relaxed">Most approaches wait for problems to get worse before intervening. We build strengths before gaps become chasms.</p> 
<p className="text-lg text-gray-700 leading-relaxed">Your child's brain is doing its most important growing right now. The connections forming today determine how they'll learn,communicate,eat,and regulate emotions for life.</p> 
<p className="text-xl font-semibold text-accent-magenta">We don't just address delays—we accelerate development by targeting the root skills that power everything else.</p> 
</motion.div> 

<div className="mb-16"> 
<h3 className="text-3xl font-bold text-center text-gray-900 mb-12 font-serif">Our Comprehensive Early Development System</h3> 
<div className="grid lg:grid-cols-3 gap-8"> 
{/* Root Skills */} 
<motion.div initial={{opacity: 0,y: 30}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{duration: 0.8,delay: 0.3}} className="bg-gradient-to-br from-accent-magenta/5 to-accent-magenta/10 p-8 rounded-2xl border border-accent-magenta/20" > 
<div className="text-center mb-8"> 
<div className="w-16 h-16 bg-accent-magenta rounded-full flex items-center justify-center mx-auto mb-4"> 
<span className="text-2xl">🧠</span> 
</div> 
<h4 className="text-2xl font-bold text-gray-900 mb-3 font-serif">Root Skills</h4> 
<p className="text-gray-600">Sensory-Motor Foundation - the core systems</p> 
</div> 
<ul className="space-y-3"> 
{rootSkills.map((skill,index)=> ( 
<motion.li key={index} initial={{opacity: 0,x: -20}} animate={inView ? {opacity: 1,x: 0} : {}} transition={{delay: 0.4 + index * 0.1}} className="flex items-start space-x-3" > 
<span className="w-2 h-2 bg-accent-magenta rounded-full mt-2 flex-shrink-0"></span> 
<span className="text-gray-700 leading-relaxed text-sm">{skill}</span> 
</motion.li> 
))} 
</ul> 
</motion.div> 

{/* Leaf Skills */} 
<motion.div initial={{opacity: 0,y: 30}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{duration: 0.8,delay: 0.4}} className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl border border-green-200" > 
<div className="text-center mb-8"> 
<div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4"> 
<span className="text-2xl">🌱</span> 
</div> 
<h4 className="text-2xl font-bold text-gray-900 mb-3 font-serif">Leaf Skills</h4> 
<p className="text-gray-600">Emerging Abilities - Making connection possible</p> 
</div> 
<ul className="space-y-3"> 
{leafSkills.map((skill,index)=> ( 
<motion.li key={index} initial={{opacity: 0,x: -20}} animate={inView ? {opacity: 1,x: 0} : {}} transition={{delay: 0.5 + index * 0.1}} className="flex items-start space-x-3" > 
<span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></span> 
<span className="text-gray-700 leading-relaxed text-sm">{skill}</span> 
</motion.li> 
))} 
</ul> 
</motion.div> 

{/* Flower Skills */} 
<motion.div initial={{opacity: 0,y: 30}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{duration: 0.8,delay: 0.5}} className="bg-gradient-to-br from-pink-50 to-pink-100 p-8 rounded-2xl border border-pink-200" > 
<div className="text-center mb-8"> 
<div className="w-16 h-16 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-4"> 
<span className="text-2xl">🌸</span> 
</div> 
<h4 className="text-2xl font-bold text-gray-900 mb-3 font-serif">Flower Skills</h4> 
<p className="text-gray-600">Blossoming Independence - Thriving,at home and beyond</p> 
</div> 
<ul className="space-y-3"> 
{flowerSkills.map((skill,index)=> ( 
<motion.li key={index} initial={{opacity: 0,x: -20}} animate={inView ? {opacity: 1,x: 0} : {}} transition={{delay: 0.6 + index * 0.1}} className="flex items-start space-x-3" > 
<span className="w-2 h-2 bg-pink-600 rounded-full mt-2 flex-shrink-0"></span> 
<span className="text-gray-700 leading-relaxed text-sm">{skill}</span> 
</motion.li> 
))} 
</ul> 
</motion.div> 
</div> 
</div> 

<motion.div initial={{opacity: 0,y: 20}} animate={inView ? {opacity: 1,y: 0} : {}} transition={{delay: 0.8}} className="text-center" > 
<div className="flex flex-col sm:flex-row gap-4 justify-center"> 
<BookingButton text="Start Building Today" /> 
<motion.a whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} href="https://www.wethebillions.com/quiz" className="btn-secondary" > 
Take Assessment Quiz 
</motion.a> 
</div> 
</motion.div> 
</div> 
</section> 
);
};

export default SolutionSection;